'use client';

import { useCallback, useState, useLayoutEffect } from 'react';

type UseRemOptions = {
  baseWidth?: number;
  baseFontSize?: number;
  debounceTime?: number;
  maxFontSize?: number;
  minFontSize?: number;
};

// 防抖函数封装
const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;

  const debouncedFunction = (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  };

  // 提供取消方法，便于在卸载时清理
  debouncedFunction.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFunction;
};

/**
 * 一个用于实现 REM 自适应布局的 React Hook。
 * @param options 配置项
 * @returns 包含当前 fontSize 和 pxToRem 转换函数的对象
 */
export const useRem = (options: UseRemOptions = {}) => {
  const [isRemReady, setIsRemReady] = useState<boolean | null>(null);
  const {
    baseWidth = 375,
    baseFontSize = 16,
    debounceTime = 100,
    maxFontSize,
    minFontSize,
  } = options;

  // 计算并设置根字体大小
  const updateRootFontSize = useCallback(() => {
    if (typeof window === 'undefined') return;

    const clientWidth = window.innerWidth;
    let remFontSize = (clientWidth / baseWidth) * baseFontSize;

    // 应用最大/最小字体大小限制
    if (minFontSize !== undefined) {
      remFontSize = Math.max(minFontSize, remFontSize);
    }
    if (maxFontSize !== undefined) {
      remFontSize = Math.min(maxFontSize, remFontSize);
    }

    // 为了避免字体大小出现过多小数位，可以进行四舍五入
    remFontSize = Math.round(remFontSize * 100) / 100;

    document.documentElement.style.fontSize = `${remFontSize}px`;
    setIsRemReady(true);
    // 可以在这里直接返回计算后的值，供外部使用
    return remFontSize;
  }, [baseWidth, baseFontSize, minFontSize, maxFontSize]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return setIsRemReady(true);

    // 初始化时立即执行一次
    updateRootFontSize();

    // 创建防抖处理函数
    const handleResize = debounce(updateRootFontSize, debounceTime);

    // 添加事件监听
    window.addEventListener('resize', handleResize);
    // 某些移动设备在旋转时不会触发 resize，最好也监听 orientationchange
    window.addEventListener('orientationchange', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      // 取消当前可能存在的防抖定时器
      handleResize.cancel();
      // 卸载时可以重置根字体大小为默认值
      document.documentElement.style.fontSize = '';
    };
  }, [updateRootFontSize, debounceTime]);

  // 返回一个对象，包含当前 fontSize 和一个 px 转 rem 的工具函数
  return {
    isRemReady,
    // 直接从 DOM 中获取当前的 fontSize，这样更准确且无需维护 state
    fontSize:
      typeof window !== 'undefined'
        ? parseFloat(getComputedStyle(document.documentElement).fontSize)
        : baseFontSize,
    // 提供一个便捷的 px 转 rem 的函数
    pxToRem: (px: number) => {
      const currentFontSize =
        typeof window !== 'undefined'
          ? parseFloat(getComputedStyle(document.documentElement).fontSize)
          : baseFontSize;
      return px / currentFontSize;
    },
  };
};
